import React, { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "../api";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";

const DEFAULT_CFG = {
    enabled: true,
    bot_name: "UCare Concierge",
    tagline: "AI wellness assistant · always-on",
    greeting: "Hi! I'm UCare Concierge. Ask me about products, ingredients, or how to leave a review.",
    suggestions: ["What helps with PCOS?", "Best product for hair fall?", "Do you ship for free?", "Cramp relief options?"],
};

/* ---- lightweight markdown: **bold**, [label](url), bullets ---- */
function renderInline(text, keyBase) {
    const parts = [];
    const re = /(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)\s]+\))/g;
    let last = 0, m, i = 0;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        const tok = m[0];
        if (tok.startsWith("**")) {
            parts.push(<strong key={`${keyBase}-b${i++}`}>{renderInline(tok.slice(2, -2), `${keyBase}-bi${i}`)}</strong>);
        } else {
            const label = tok.slice(1, tok.indexOf("]"));
            const url = tok.slice(tok.indexOf("](") + 2, -1);
            parts.push(
                url.startsWith("/") ? (
                    <Link key={`${keyBase}-l${i++}`} to={url} className="underline font-medium text-[var(--taupe-deep)] hover:text-[var(--ink)]">{label}</Link>
                ) : (
                    <a key={`${keyBase}-a${i++}`} href={url} target="_blank" rel="noreferrer" className="underline font-medium">{label}</a>
                )
            );
        }
        last = m.index + tok.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

function BotMessage({ content }) {
    const blocks = [];
    let list = null;
    content.split("\n").forEach((line) => {
        const t = line.trim();
        if (/^([-•*]|\d+[.)])\s+/.test(t)) {
            if (!list) { list = []; blocks.push({ type: "ul", items: list }); }
            list.push(t.replace(/^([-•*]|\d+[.)])\s+/, ""));
        } else {
            list = null;
            if (t) blocks.push({ type: "p", text: t });
        }
    });
    return (
        <div className="space-y-1.5">
            {blocks.map((b, i) =>
                b.type === "ul" ? (
                    <ul key={i} className="list-disc pl-4 space-y-1">
                        {b.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}`)}</li>)}
                    </ul>
                ) : (
                    <p key={i}>{renderInline(b.text, `p${i}`)}</p>
                )
            )}
        </div>
    );
}

export default function Chatbot() {
    const [cfg, setCfg] = useState(DEFAULT_CFG);
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem("uce_chat_sid") || "");
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const productId = (location.pathname.startsWith("/product/") ? location.pathname.split("/").pop() : null);
    const scrollRef = useRef(null);

    useEffect(() => {
        api.get("/settings").then((r) => {
            const cb = r.data?.chatbot;
            if (cb) {
                setCfg({
                    ...DEFAULT_CFG,
                    ...cb,
                    greeting: cb.greeting || DEFAULT_CFG.greeting,
                    suggestions: cb.suggestions?.length ? cb.suggestions : DEFAULT_CFG.suggestions,
                });
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
    }, [messages, open]);

    // Hide chatbot on admin routes / when admin disabled it (after hooks to satisfy rules-of-hooks)
    if (location.pathname.startsWith("/admin")) return null;
    if (cfg.enabled === false) return null;

    const allMessages = [{ role: "assistant", content: cfg.greeting }, ...messages];

    const send = async (text) => {
        const trimmed = (text || input).trim();
        if (!trimmed || loading) return;
        setMessages((m) => [...m, { role: "user", content: trimmed }]);
        setInput("");
        setLoading(true);
        try {
            const { data } = await api.post("/chat", {
                message: trimmed,
                session_id: sessionId || null,
                product_id: productId || null,
            });
            if (data.session_id && !sessionId) {
                setSessionId(data.session_id);
                localStorage.setItem("uce_chat_sid", data.session_id);
            }
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again in a moment." }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen((o) => !o)}
                className="fixed right-5 z-50 w-14 h-14 rounded-full bg-[var(--taupe)] text-white shadow-lg flex items-center justify-center hover:bg-[var(--taupe-deep)] transition"
                style={{ bottom: "calc(20px + var(--fab-offset, 0px) + env(safe-area-inset-bottom, 0px))", transition: "bottom 0.25s ease" }}
                aria-label="Open chat"
                data-testid="chatbot-toggle"
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
            </button>

            {open && (
                <div
                    className="fixed right-3 sm:right-5 z-50 w-[calc(100vw-24px)] sm:w-[380px] max-w-[420px] h-[70vh] max-h-[560px] card-glass flex flex-col overflow-hidden shadow-2xl"
                    data-testid="chatbot-panel"
                    style={{
                        borderRadius: 22,
                        bottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
                        background: "linear-gradient(180deg, #fdfaf5, #fbf0e8)",
                    }}
                >
                    <div className="px-4 py-3 border-b border-[var(--line-soft)] flex items-center gap-3 bg-[var(--bg-soft)]">
                        <div className="w-9 h-9 rounded-full bg-[var(--taupe)] text-white grid place-items-center">
                            <Sparkles size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-serif text-[16px] text-[var(--ink)] leading-tight" data-testid="chatbot-bot-name">{cfg.bot_name}</div>
                            <div className="text-[11px] text-[var(--body)]">{cfg.tagline}</div>
                        </div>
                        <button onClick={() => setOpen(false)} className="text-[var(--ink-soft)] hover:text-[var(--ink)]" aria-label="Close">
                            <X size={18} />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chatbot-messages">
                        {allMessages.map((m, i) => (
                            <div key={`${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13.5px] leading-relaxed ${
                                        m.role === "user"
                                            ? "bg-[var(--taupe)] text-white rounded-br-sm"
                                            : "bg-white border border-[var(--line-soft)] text-[var(--ink)] rounded-bl-sm"
                                    }`}
                                    data-testid={`chatbot-msg-${m.role}`}
                                >
                                    {m.role === "assistant" ? <BotMessage content={m.content} /> : m.content}
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start" data-testid="chatbot-loading">
                                <div className="bg-white border border-[var(--line-soft)] px-3 py-2 rounded-2xl rounded-bl-sm">
                                    <div className="flex gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--taupe)] animate-pulse" />
                                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--taupe)] animate-pulse" style={{ animationDelay: "0.15s" }} />
                                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--taupe)] animate-pulse" style={{ animationDelay: "0.3s" }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {messages.length <= 1 && (
                        <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                            {cfg.suggestions.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => send(s)}
                                    className="text-[11px] px-2.5 py-1 rounded-full bg-[var(--bg-alt)] text-[var(--ink-soft)] hover:bg-[var(--rose)]/40 transition"
                                    data-testid={`chatbot-suggestion-${s.slice(0, 12)}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="border-t border-[var(--line-soft)] p-2 flex gap-2 bg-[var(--bg-soft)]"
                    >
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about products, ingredients…"
                            className="flex-1 px-3 py-2 rounded-full border border-[var(--line)] text-[13px] bg-white focus:outline-none focus:border-[var(--taupe)]"
                            data-testid="chatbot-input"
                            disabled={loading}
                        />
                        <button
                            type="submit"
                            disabled={loading || !input.trim()}
                            className="w-9 h-9 rounded-full bg-[var(--taupe)] text-white grid place-items-center disabled:opacity-40 hover:bg-[var(--taupe-deep)] transition"
                            data-testid="chatbot-send"
                            aria-label="Send"
                        >
                            <Send size={15} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
